import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';
import { DelayRiskExplanation } from '@apm/contracts';
import { AiSkillPanel } from '../../ai/components/ai-skill-panel';

/**
 * Giải thích điểm rủi ro trễ — APM-AI-r, nút ở tab Báo cáo.
 *
 * Bảng chỉ báo và điểm số do hệ thống chấm mỗi đêm (`APM-AI-q`) đã có đủ **số**
 * ngay trên trang này; thứ nó thiếu là **lời**. Nên đây là một mục đọc thêm
 * nằm cạnh bảng, không phải một cách tính điểm thứ hai — nếu hai chỗ cho hai
 * con số khác nhau thì người đọc không biết tin cái nào.
 */
export function DelayRiskSection({
  projectId,
  defaultOpen = false,
}: {
  readonly projectId: string;
  /** Mở sẵn — dùng khi khối này **là** nội dung của một hộp thoại. */
  readonly defaultOpen?: boolean;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <AiSkillPanel
      defaultOpen={defaultOpen}
      /*
        Không có đường làm tay tương đương — APM-PSET-k. Bảng chỉ báo trên trang
        này đã có đủ **số**; thứ khối này thêm là **lời** — điều gì đang kéo điểm
        xuống và làm gì trước. Chỉ sang bảng chỉ báo là đưa người dùng tới đúng
        thứ họ vừa nhìn và vẫn chưa hiểu.
      */
      offlineMode="needKey"
      skill="explain_delay_risk"
      projectId={projectId}
      icon={Gauge}
      title={t('delayRisk.title')}
      help={t('delayRisk.help')}
      label={t('delayRisk.run')}
      prompt={t('delayRisk.prompt')}
      schema={DelayRiskExplanation}
      routeParams={{ projectId }}
      render={(content) => (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            <span className="font-medium tabular-nums">{content.score}</span>
            <span className="text-muted-foreground"> / 100</span>
          </p>
          <p className="whitespace-pre-wrap text-sm">{content.narrative}</p>

          {content.actions.length > 0 ? (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
              {content.actions.map((action, index) => (
                <li key={index}>{action.text}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    />
  );
}
